import * as _ from "lodash";
import numeral from "numeral";
import { IModelNode, ModelNode } from "../smartDrawings";

/**
 * Logic for finding needed properties for the filtered properties tool.
 */
export namespace PropertyModel {

    // A cache of fetched node properties indexed by modelId and nodeId.
    export type PropertyCache = { [key: string]: Autodesk.Viewing.Property[]; };

    export type PropertySelector = { displayCategory: string, finalCategory?: string, displayNames: NamePair[], summed?: boolean };
    export type PropertyTypeSelector = { displayCategory: string, displayName: string, displayValue: string };
    export type PropertyCriterion = { types: string[], typeSelectors: PropertyTypeSelector[], properties: PropertySelector[] }
    export type TitleSelector = { displayCategory: string, displayName: string };
    export type Formatter = { unit?: string, format?: string, exponent?: number };
    export type NamePair = { original: string, final?: string, formatter?: Formatter };
 
    /**
     * A utility that traverses the model node hierarchy upwards from the given node to find properties according to the given search criteria.
     * The found properties are ordered, first by the order of the criteria themselves, then by the order of the property display names inside the criterion.
     */
    export class PropertyFinder {

        private readonly parentIdent = { displayCategory: "__parent__", displayName: "parent" };

        // A function that does the actual property fetching from model is passed here so we're not dependent on Viewer stuff.
        private readonly getPropertiesFromModel: (modelNode: IModelNode) => Promise<Autodesk.Viewing.Property[]>;

        constructor(getProperties: (modelNode: IModelNode) => Promise<Autodesk.Viewing.Property[]>) {
            this.getPropertiesFromModel = getProperties;
        }

        /**
         * Divide a property selector that may have multiple displayNames under a displayCategory into multiple category-name pair selectors for simplicity.
         */
        private flattenPropertySelector(selector: PropertySelector): PropertySelector[] {
            return selector.displayNames.map(name => ({
                displayCategory: selector.displayCategory,
                finalCategory: selector.finalCategory,
                displayNames: [name],
                summed: selector.summed
            }));
        }

        /**
         * Simplify the criteria to one property per type definition so iterating through them is less complex. (See end of file for example.)
         */
        private flattenCriteria(criteria: PropertyCriterion[]): PropertyCriterion[] {
            const withSingleProperties: PropertyCriterion[] = criteria.map(cri => ({
                ...cri,
                properties: _.reduce(cri.properties,
                    (acc, prop) => {
                        return [...acc, ...this.flattenPropertySelector(prop)];
                    },
                    [])
            }));
            
            const flat: PropertyCriterion[] = _.reduce(withSingleProperties,
                (acc, cri) => {
                    return [...acc, ...cri.properties.map(prop => ({ ...cri, properties: [prop] }))];
                },
                []);
            return flat;
        }

        // Make key for dictionary in PropertyCache
        private getPropertyCacheKey = (modelNode: IModelNode): string => `${modelNode.modelId}.${modelNode.nodeId}`;

        /**
         * Search one node for properties according to the given criteria.
         */
        async getNodeProperties(modelNode: IModelNode, otherIds: any[], criteria: PropertyCriterion[], titleSelector?: PropertyModel.TitleSelector): Promise<{ properties: Autodesk.Viewing.Property[], title?: string }> {
          
            // TODO set and flatten criteria once
            const flatCriteria = this.flattenCriteria(criteria);
            let nodePropertyCache: PropertyCache = {};
            let properties: Autodesk.Viewing.Property[] = [];
         
            for (let i = 0; i < flatCriteria.length; i++) {
                const cri = flatCriteria[i];
                const result = await this.handleSingleProperty(modelNode, cri, nodePropertyCache);
                const property = result.property;
                nodePropertyCache = result.nodePropertyCache;
                if (property != null) {
                    let valueSum: number = !_.isEmpty(otherIds) && cri.properties[0].summed ? Number(property.displayValue) : null;
                    if (valueSum != null) {
                        for (let oid of otherIds) {
                            let otherModelNode = new ModelNode(modelNode.modelId, Number(oid));
                            const otherResult = await this.handleSingleProperty(otherModelNode, cri, nodePropertyCache);
                            nodePropertyCache = otherResult.nodePropertyCache;
                            valueSum = valueSum + Number(otherResult.property.displayValue);
                        }
                    }

                    const formatter = cri.properties[0].displayNames[0].formatter;

                    if (valueSum != null) {
                        const sumProperty: Autodesk.Viewing.Property = {
                            ...property,
                            displayName: property.displayName,
                            displayValue: valueSum + ""
                        };
                        properties = [...properties, this.formatProperty(sumProperty, formatter)];
                    } else if (!cri.properties[0].summed) {
                        properties = [...properties, this.formatProperty(property, formatter)];
                    }
                }
            }
            return {
                properties: properties,
                title: this.getPropertyTitle(nodePropertyCache[this.getPropertyCacheKey(modelNode)], titleSelector)
            };
        }

        formatProperty(property: Autodesk.Viewing.Property, formatter?: PropertyModel.Formatter): Autodesk.Viewing.Property {
            if (formatter == null) {
                return property;
            }
            const { unit, format, exponent } = formatter;
            let formatted = exponent != null ? Number(property.displayValue) * Math.pow(10, exponent) : Number(property.displayValue);
            formatted = format != null ? numeral(formatted).format(format) : formatted;

            const formattedProperty: Autodesk.Viewing.Property = {
                ...property,
                displayValue: formatted + "",
                units: unit || property.units
            }
            return formattedProperty;
        }

        getPropertyTitle(properties: Autodesk.Viewing.Property[], titleSelector: PropertyModel.TitleSelector): string {
            const property = titleSelector && _.find(properties,
                prop => _.toLower(prop.displayCategory) === _.toLower(titleSelector.displayCategory) &&
                    _.toLower(prop.displayName) === _.toLower(titleSelector.displayName));
            return property != null ? property.displayValue.toString() : null;
        }

        private typeMatches(properties: Autodesk.Viewing.Property[], criteria: PropertyCriterion): boolean {
            if (criteria.typeSelectors && criteria.typeSelectors.length > 0) {
                return _.some(properties,
                    prop => {
                        return _.some(criteria.typeSelectors, selector => {
                            return prop.displayCategory === selector.displayCategory &&
                                prop.displayName === selector.displayName &&
                                prop.displayValue.toString() === selector.displayValue;
                        });
                    });
            }

            return _.some(properties,
                prop => {
                    return prop.displayCategory === "Item" &&
                        prop.displayName === "Type" &&
                        _.some(criteria.types, type => _.toLower(type) === _.toLower(prop.displayValue.toString()));
                });
        }

        private filterProperty(properties: Autodesk.Viewing.Property[], criterion: PropertyCriterion): Autodesk.Viewing.Property {
            if (this.typeMatches(properties, criterion)) {
                return _.find(properties,
                    prop => _.toLower(prop.displayCategory) === _.toLower(criterion.properties[0].displayCategory) && _.toLower(prop.displayName) === _.toLower(criterion.properties[0].displayNames[0].original)
                ) as Autodesk.Viewing.Property;
            } else {
                return null;
            }
        }

        /**
         * Search for properties given by the criterion from the current node. Cache properties by nodeId so that we don't need to make the expensive(?) async model fetches so much.
         * If the property is not found, search recursively from parent.
         */
        private async handleSingleProperty(currentModelNode: IModelNode,
            criterion: PropertyCriterion,
            nodePropertyCache: PropertyCache): Promise<{nodePropertyCache: PropertyCache, property: Autodesk.Viewing.Property}> {

            const nodeProperties = await this.fetchProperties(currentModelNode, nodePropertyCache);
            //const otherProperties = await Promise.all(otherIds.map(id => this.fetchProperties(id, nodePropertyCache)));
            
            const updatedCache = { ...nodePropertyCache };
            updatedCache[this.getPropertyCacheKey(currentModelNode)] = nodeProperties;

            const foundProperty = this.filterProperty(nodeProperties, criterion);
            
            if (!_.isEmpty(foundProperty)) {
                return { nodePropertyCache: updatedCache, property: this.transformProperty(foundProperty, criterion) };
            } else {
                const parent = _.find(nodeProperties, prop => this.isParentProperty(prop));
                if (parent != null) {
                    let parentModelNode = new ModelNode(currentModelNode.modelId, Number(parent.displayValue))
                    return this.handleSingleProperty(parentModelNode, criterion, updatedCache);
                } else {
                    return { nodePropertyCache: updatedCache, property: null };
                }
            }
        }

        private async fetchProperties(modelNode: IModelNode, nodePropertyCache: PropertyModel.PropertyCache) {
            let nodeProperties = nodePropertyCache[this.getPropertyCacheKey(modelNode)];
            if (nodeProperties == null) {
                const properties = await this.getPropertiesFromModel(modelNode);
                nodeProperties = _.filter(properties, prop => !prop.hidden || this.isParentProperty(prop));
            }
            return nodeProperties;
        }

        private transformProperty(property: Autodesk.Viewing.Property, criterion: PropertyModel.PropertyCriterion): Autodesk.Viewing.Property {

            return {
                ...property,
                displayCategory: criterion.properties[0].finalCategory != null
                    ? criterion.properties[0].finalCategory
                    : property.displayCategory,
                displayName: criterion.properties[0].displayNames[0].final != null
                    ? criterion.properties[0].displayNames[0].final
                    : property.displayName
            };
        }

        private isParentProperty(property: Autodesk.Viewing.Property): boolean {
            return property.displayCategory === this.parentIdent.displayCategory &&
                property.displayName === this.parentIdent.displayName;
        }
    }
}

/**
 * Criteria flattening example.
 *
 * [
        {
            types:
                ["IFCPLATE", "IFCCOLUMN"], 
            properties: [
                {
                    displayCategory: "Item",
                    displayNames: ["Name", "Material"]
                },
                {
                    displayCategory: "IFC",
                    displayNames: ["GUID"]
                }
            ]
        },
        {
            types:
                ["IFCBEAM"],
            properties: [
                {
                    displayCategory: "Dimensions",
                    displayNames: ["Length"]
                }
            ]
        }
    ]

==>

[
        {
            types:
                ["IFCPLATE", "IFCCOLUMN"], 
            properties: [
                {
                    displayCategory: "Item",
                    displayNames: ["Name"]
                }
            ]
        },
        {
            types:
                ["IFCPLATE", "IFCCOLUMN"],
            properties: [
                {
                    displayCategory: "Item",
                    displayNames: ["Material"]
                }
            ]
        },
        {
            types:
                ["IFCPLATE", "IFCCOLUMN"],
            properties: [
                {
                    displayCategory: "IFC",
                    displayNames: ["GUID"]
                }
            ]
        },
        {
            types:
                ["IFCBEAM"],
            properties: [
                {
                    displayCategory: "Dimensions",
                    displayNames: ["Length"]
                }
            ]
        },
    ]
 */